import { z } from 'zod';
import { TaskShape } from './task.js';

/**
 * work-tasks-performance B8/F12 — схема СТРОКИ СПИСКА задач.
 *
 * ЧТО ЛЕЧИТ. Список отдавал полную задачу с описанием в каждой строке: на 500
 * строках это мегабайты payload'а, а на клиенте — построчный разбор схемы с
 * четырьмя `.refine()`, то есть четыре перекрёстные проверки × число строк на
 * главном потоке. Ни описание, ни эти проверки строке таблицы не нужны: она
 * рисует название, статус, исполнителя, даты.
 *
 * ЧТО ЗДЕСЬ ЕСТЬ:
 *   • те же поля, что у `Task` (через общий `TaskShape` — второй копии списка
 *     полей не заводим, она разъехалась бы на первом новом поле);
 *   • `has_description` — признак «описание есть», по нему рисуется маркер;
 *   • БЕЗ `.refine()`: они проверяют инварианты, которые сервер гарантирует при
 *     записи, и на выдаче стоят по проверке на строку.
 *
 * ПРО ОПИСАНИЯ И ДВУХРЕЛИЗНОСТЬ. `description_json` / `description_text`
 * остаются в схеме с дефолтами, но **читать их из строки списка нельзя**:
 * в релизе 1 сервер их ещё шлёт, в релизе 2 (B9) перестаёт, и вместо текста
 * придёт пустая строка — тихо и правдоподобно. Кому нужен текст описания, тот
 * грузит задачу целиком (`GET /tasks/:id`). Дефолты здесь именно ради этого
 * перехода: старая вкладка между релизами не падает, а новая не ломается,
 * когда поля исчезнут.
 *
 * ОДНА СХЕМА НА ТРИ ВЫДАЧИ `[Б16]` — list, gantt и search. Третью не заводить.
 */
export const TaskListItem = TaskShape.extend({
  /** Признак наличия описания. Считает сервер — строке достаточно знать «да/нет». */
  has_description: z.boolean().default(false),
  /** @deprecated В списочной выдаче не читать — см. заголовок файла (B9). */
  description_json: z.unknown().optional(),
  /** @deprecated В списочной выдаче не читать — после релиза 2 всегда пустая. */
  description_text: z.string().default(''),
  /**
   * B10 (work-tasks-user-feedback §3.4) — роллап папки от ПОЛНОГО набора детей.
   *
   * Фронт считал его по видимым строкам, а дефолтный фильтр вырезает завершённых:
   * человек закрывал задачу — и процент этапа падал. Полный набор знает только
   * backend, поэтому число приходит отсюда. `null` — учитываемых листьев нет
   * (все отменены или детей нет вовсе): это прочерк, а не «0%».
   *
   * Nullable-**optional**: старая вкладка в окне выката поля не получает и не
   * падает, а фронт держит собственный fallback-роллап (F16).
   */
  subtree_progress: z.string().nullable().optional(),
  /** Сколько листьев вошло в агрегат — по нему FE подписывает роллап. */
  subtree_leaf_count: z.number().int().nonnegative().optional(),
  /**
   * F48 (WTP-B3) — пользовательские поля едут в строке, только когда их колонки
   * видимы (`fields`); без них строка — пустой набор, а не ошибка разбора.
   */
  custom_fields: z.record(z.string(), z.unknown()).default({}),
});
export type TaskListItem = z.infer<typeof TaskListItem>;
